import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Plus, 
  Trash2, 
  Edit3, 
  Save, 
  Clock, 
  X
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import './FormsUnified.css';

export default function AdmissionsKeyDatesForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState({ 
    title: '', 
    subtitle: '', 
    description: '', 
    keyDates: [] 
  });
  const [pendingFields, setPendingFields] = useState({ 
    title: false, 
    subtitle: false, 
    description: false 
  });
  const [pendingDates, setPendingDates] = useState({});
  const [originalData, setOriginalData] = useState({ ...formData });
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});

  // Modal State
  const [isAddingDate, setIsAddingDate] = useState(false);
  const [editingDateIndex, setEditingDateIndex] = useState(null);
  const [modalDateData, setModalDateData] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadData = async () => {
    setLoading(true);
    try {
      let section = null;

      // 1. Fetch admissions sections via admin endpoint
      try {
        const response = await fetch(`${apiBase}/admissions/cms/sections`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const result = await response.json();
          if (result.ok && Array.isArray(result.data)) {
            section = result.data.find(s => s.section_type === 'admissions-key-dates');
          }
        }
      } catch (err) {
        console.warn('[AdmissionsKeyDatesForm] Admin fetch warning:', err.message);
      }

      // 2. Fallback to public query if needed
      if (!section) {
        try {
          const pubRes = await fetch(`${apiBase}/admissions/cms/sections?published=true`, {
            credentials: 'include',
            headers: getAuthHeaders()
          });
          if (pubRes.ok) {
            const pubResult = await pubRes.json();
            if (pubResult.ok && Array.isArray(pubResult.data)) {
              section = pubResult.data.find(s => s.section_type === 'admissions-key-dates');
            }
          }
        } catch (pubErr) {
          console.warn('[AdmissionsKeyDatesForm] Public fetch warning:', pubErr.message);
        }
      }

      if (!section) {
        throw new Error('Admissions Timeline & Key Dates section not found');
      }

      setSectionId(section.id);

      let content = section.content_published || section.content || {};
      if (typeof content === 'string') {
        try { content = JSON.parse(content); } catch (e) { content = {}; }
      }
      const en = content?.en || content || {};
      
      const parseDates = (dates) => {
        if (!Array.isArray(dates)) return [];
        return dates
          .filter(d => d && typeof d === 'object')
          .map(d => {
            let dateStart = '';
            let dateEnd = '';
            
            if (d.dateRange) {
              const parts = d.dateRange.split(' to ');
              dateStart = parts[0]?.trim() || '';
              dateEnd = parts[1]?.trim() || '';
            } else if (d.date) {
              const dashParts = d.date.split(' - ');
              if (dashParts.length > 1) {
                dateStart = dashParts[0]?.trim() || '';
                dateEnd = dashParts[1]?.trim() || '';
              } else {
                dateStart = d.date;
                dateEnd = d.date;
              }
            }
            
            return {
              title: d.title || '',
              dateStart: d.dateStart || dateStart,
              dateEnd: d.dateEnd || (dateEnd !== dateStart ? dateEnd : ''),
              description: d.description || '',
              tag: d.tag || '',
              status: d.status || 'auto'
            };
          });
      };
      
      const rawDates = en.dates || en.keyDates || [];
      const cleanDates = parseDates(rawDates);

      const initialData = {
        title: en.title || '',
        subtitle: en.subtitle || en.kicker || '',
        description: en.description || '',
        keyDates: cleanDates
      };

      const pMap = { title: false, subtitle: false, description: false };
      const dMap = {};

      // 3. Check for pending changes in workflow
      try {
        const pRes = await fetch(`${apiBase}/cms/section/${section.id}/with-pending`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (pRes.ok) {
          const pResult = await pRes.json();
          if (pResult.success && Array.isArray(pResult.data?.pending_changes)) {
            pResult.data.pending_changes.forEach(c => {
              if (c.status === 'pending' && (c.locale === 'en' || !c.locale)) {
                let val = c.new_value;
                if (typeof val === 'string') {
                  try { val = JSON.parse(val); } catch (e) {}
                }

                if (['title', 'subtitle', 'description'].includes(c.field_id)) {
                  pMap[c.field_id] = true;
                  if (typeof val === 'string') {
                    initialData[c.field_id] = val;
                  }
                } else if (c.field_id && (c.field_id.includes('keyDates.') || c.field_id.includes('dates.'))) {
                  const m = c.field_id.match(/(?:keyDates|dates)\.(\d+)\.(add|update|delete)/);
                  if (m) {
                    const dateIdx = parseInt(m[1], 10);
                    const op = m[2];
                    dMap[`date_${dateIdx}`] = true;
                    if (op === 'delete') {
                      dMap[`date_${dateIdx}_delete`] = true;
                    } else if (op === 'add') {
                      dMap[`date_${dateIdx}_add`] = true;
                      if (val && !initialData.keyDates[dateIdx]) {
                        initialData.keyDates.push({
                          title: val.title || '',
                          dateStart: val.dateStart || val.date || '',
                          dateEnd: val.dateEnd || '',
                          description: val.description || '',
                          tag: val.tag || '',
                          status: val.status || 'auto'
                        });
                      }
                    } else if (op === 'update') {
                      if (val && initialData.keyDates[dateIdx]) {
                        initialData.keyDates[dateIdx] = { 
                          ...initialData.keyDates[dateIdx], 
                          ...val,
                          dateStart: val.dateStart || val.date || initialData.keyDates[dateIdx].dateStart,
                          dateEnd: val.dateEnd !== undefined ? val.dateEnd : initialData.keyDates[dateIdx].dateEnd
                        };
                      }
                    }
                  }
                }
              }
            });
          }
        }
      } catch (pErr) {
        console.warn('[AdmissionsKeyDatesForm] Pending changes check warning:', pErr.message);
      }

      setFormData(initialData);
      setOriginalData(JSON.parse(JSON.stringify(initialData)));
      setPendingFields(pMap);
      setPendingDates(dMap);
    } catch (err) {
      console.error('[AdmissionsKeyDatesForm] Load error:', err.message);
      toast.error('Failed to load admissions key dates: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitField = async (name, label) => {
    if (!sectionId) { 
      toast.error('Section ID not available'); 
      return; 
    }
    if (formData[name] === originalData[name]) { 
      toast.warning('No changes to submit'); 
      return; 
    }

    setSubmitting(p => ({ ...p, [name]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: name,
          field_name: label || `Admissions Timeline ${name.replace(/_/g, ' ')}`,
          old_value: originalData[name] || '',
          new_value: formData[name] || '',
          locale: 'en'
        })
      });
      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`Submitted ${label || name} for Principal approval`);
        setPendingFields(p => ({ ...p, [name]: true }));
        setOriginalData(p => ({ ...p, [name]: formData[name] }));
      } else {
        if (res.status === 409) {
          toast.warning(`A pending proposal for ${label || name} is already awaiting review`);
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(p => ({ ...p, [name]: false }));
    }
  };

  // Open Add Date Modal
  const handleOpenAddDate = () => {
    if (formData.keyDates.length >= 15) {
      toast.warning('Maximum 15 timeline milestones allowed');
      return;
    }
    const newDate = { 
      title: '', 
      dateStart: '', 
      dateEnd: '', 
      description: '', 
      tag: '',
      status: 'auto'
    };
    setIsAddingDate(true);
    setEditingDateIndex(formData.keyDates.length);
    setModalDateData(newDate);
  };

  // Open Edit Date Modal
  const handleOpenEditDate = (idx) => {
    const target = formData.keyDates[idx];
    if (!target) return;
    setIsAddingDate(false);
    setEditingDateIndex(idx);
    setModalDateData({ ...target });
  };

  const handleCloseModal = () => {
    setIsAddingDate(false);
    setEditingDateIndex(null);
    setModalDateData(null);
  };

  const handleModalDateChange = (field, value) => {
    if (!modalDateData) return;
    setModalDateData(prev => ({ ...prev, [field]: value }));
  };

  // Submit Date from Modal (Add or Update)
  const handleSaveDateModal = async () => {
    if (!sectionId) { 
      toast.error('Section ID not available'); 
      return; 
    }
    if (!modalDateData || !modalDateData.title?.trim()) { 
      toast.warning('Milestone title is required'); 
      return; 
    }

    const idx = editingDateIndex;
    const isNew = isAddingDate;
    const op = isNew ? 'add' : 'update';
    const submitKey = isNew ? 'add_date' : `date_${idx}`;

    setSubmitting(p => ({ ...p, [submitKey]: true }));

    const payload = {
      title: modalDateData.title.trim(),
      dateStart: modalDateData.dateStart || '',
      dateEnd: modalDateData.dateEnd || '',
      date: modalDateData.dateEnd && modalDateData.dateEnd !== modalDateData.dateStart
        ? `${modalDateData.dateStart} - ${modalDateData.dateEnd}`
        : (modalDateData.dateStart || ''),
      dateRange: modalDateData.dateEnd && modalDateData.dateEnd !== modalDateData.dateStart
        ? `${modalDateData.dateStart} to ${modalDateData.dateEnd}`
        : '',
      description: modalDateData.description || '',
      tag: modalDateData.tag || '',
      status: modalDateData.status || 'auto'
    };

    const fieldId = `keyDates.${idx}.${op}`;
    const fieldName = `${isNew ? 'Add' : 'Update'} Admissions Milestone: ${payload.title}`;
    const oldValue = isNew ? null : (originalData.keyDates[idx] || {});

    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: fieldId,
          field_name: fieldName,
          old_value: oldValue,
          new_value: payload,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || res.ok) {
        toast.success(`Milestone "${payload.title}" submitted for Principal approval`);
        setPendingDates(p => ({
          ...p,
          [`date_${idx}`]: true,
          [`date_${idx}_add`]: isNew
        }));

        if (isNew) {
          setFormData(prev => ({
            ...prev,
            keyDates: [...prev.keyDates, { ...modalDateData }]
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            keyDates: prev.keyDates.map((d, i) => (i === idx ? { ...modalDateData } : d))
          }));
        }

        handleCloseModal();
      } else {
        if (res.status === 409) {
          toast.warning(`A pending proposal for this milestone is already awaiting review`);
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error submitting milestone: ' + err.message);
    } finally {
      setSubmitting(p => ({ ...p, [submitKey]: false }));
    }
  };

  // Propose Deletion of Milestone (Submitted to Principal!)
  const handleDeleteDate = async (idx) => {
    const targetDate = formData.keyDates[idx];
    if (!targetDate) return;

    if (!window.confirm(`Are you sure you want to propose deleting "${targetDate.title || `Milestone ${idx + 1}`}"? This will require Principal approval.`)) {
      return;
    }

    if (!sectionId) {
      toast.error('Section ID not available');
      return;
    }

    const key = `del_date_${idx}`;
    setSubmitting(p => ({ ...p, [key]: true }));

    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `keyDates.${idx}.delete`,
          field_name: `Delete Admissions Milestone: ${targetDate.title || `Milestone ${idx + 1}`}`,
          old_value: targetDate,
          new_value: null,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || res.ok) {
        toast.success(`Deletion of "${targetDate.title || `Milestone ${idx + 1}`}" submitted for Principal approval`);
        setPendingDates(p => ({
          ...p,
          [`date_${idx}`]: true,
          [`date_${idx}_delete`]: true
        }));
      } else {
        if (res.status === 409) {
          toast.warning(`A pending proposal for this milestone is already awaiting review`);
        } else {
          toast.error(result.error || result.message || 'Delete proposal failed');
        }
      }
    } catch (err) {
      toast.error('Error submitting delete proposal: ' + err.message);
    } finally {
      setSubmitting(p => ({ ...p, [key]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading admissions timeline & key dates...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Header */}
      <div className="unified-header">
        <div className="unified-header-icon">
          <Calendar size={22} />
        </div>
        <div>
          <h2 className="unified-title">Admissions Timeline & Key Dates</h2>
          <p className="unified-subtitle">
            Manage open application windows, assessment schedules, and enrollment deadlines
          </p>
        </div>
      </div>

      {/* Section Subtitle Card */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Section Tag / Subtitle</label>
            <p className="unified-field-desc">Kicker label above the timeline heading (e.g. Key Dates or Timeline)</p>
          </div>
          {pendingFields.subtitle && (
            <span className="unified-pending-pill">
              <Clock size={11} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.subtitle}
            onChange={(e) => handleFieldChange('subtitle', e.target.value)}
            disabled={submitting.subtitle || pendingFields.subtitle}
            placeholder="e.g. Key Dates"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('subtitle', 'Timeline Subtitle')}
            disabled={submitting.subtitle || pendingFields.subtitle || formData.subtitle === originalData.subtitle}
          >
            <Save size={14} />
            {submitting.subtitle ? 'Submitting...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Section Title Card */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Main Section Title</label>
            <p className="unified-field-desc">Headline introducing the admissions calendar</p>
          </div>
          {pendingFields.title && (
            <span className="unified-pending-pill">
              <Clock size={11} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={submitting.title || pendingFields.title}
            placeholder="e.g. Admissions Timeline & Key Dates"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title', 'Timeline Title')}
            disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
          >
            <Save size={14} />
            {submitting.title ? 'Submitting...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Section Description Card */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Section Description</label>
            <p className="unified-field-desc">Introductory text advising families to mark key milestones</p>
          </div>
          {pendingFields.description && (
            <span className="unified-pending-pill">
              <Clock size={11} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
          <textarea
            className="unified-textarea"
            rows={3}
            value={formData.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            disabled={submitting.description || pendingFields.description}
            placeholder="e.g. Mark your calendar with key admission dates to stay on track throughout the enrollment process."
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('description', 'Timeline Description')}
            disabled={submitting.description || pendingFields.description || formData.description === originalData.description}
          >
            <Save size={14} />
            {submitting.description ? 'Submitting...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Key Dates List Card */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Calendar Milestones ({formData.keyDates.length})</label>
            <p className="unified-field-desc">Scheduled events and deadline intervals displayed on the admissions timeline</p>
          </div>
          <button
            type="button"
            className="unified-secondary-btn"
            onClick={handleOpenAddDate}
          >
            <Plus size={14} /> Add Milestone
          </button>
        </div>

        {formData.keyDates.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '36px 20px', color: '#64748b' }}>
            <Calendar size={32} style={{ margin: '0 auto 10px', opacity: 0.5 }} />
            <p style={{ margin: 0, fontWeight: 600 }}>No timeline dates configured</p>
            <p style={{ margin: '4px 0 0', fontSize: '13px' }}>Click "Add Milestone" above to schedule an admissions deadline</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '10px' }}>
            {formData.keyDates.map((date, idx) => {
              const key = `date_${idx}`;
              const isPending = pendingDates[key];
              const isPendingDelete = pendingDates[`date_${idx}_delete`];
              const isPendingAdd = pendingDates[`date_${idx}_add`];

              return (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '14px 18px',
                    background: isPendingDelete ? '#fff1f2' : '#f8fafc',
                    borderRadius: '12px',
                    border: `1px solid ${isPendingDelete ? '#fecdd3' : '#e2e8f0'}`,
                    transition: 'all 0.2s ease',
                    opacity: isPendingDelete ? 0.8 : 1
                  }}
                >
                  {/* Date Badge / Tag */}
                  <div
                    style={{
                      width: '84px',
                      height: '62px',
                      borderRadius: '10px',
                      background: isPendingDelete ? '#fee2e2' : '#eff6ff',
                      color: isPendingDelete ? '#ef4444' : '#1d4ed8',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      fontWeight: 800,
                      border: `1px solid ${isPendingDelete ? '#fca5a5' : '#dbeafe'}`
                    }}
                  >
                    <span style={{ fontSize: '13.5px', textTransform: 'uppercase', letterSpacing: '0.02em', textAlign: 'center', padding: '0 4px' }}>
                      {date.tag || 'Date'}
                    </span>
                    <span style={{ fontSize: '10.5px', color: isPendingDelete ? '#dc2626' : '#64748b', fontWeight: 600 }}>
                      #{idx + 1}
                    </span>
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                      <h4 style={{ margin: 0, fontSize: '15.5px', fontWeight: 800, color: '#0f172a' }}>
                        {date.title || 'Untitled Milestone'}
                      </h4>

                      {date.dateStart && (
                        <span
                          style={{
                            padding: '2px 8px',
                            borderRadius: '6px',
                            background: '#f1f5f9',
                            color: '#475569',
                            fontSize: '11px',
                            fontWeight: 600
                          }}
                        >
                          {date.dateStart} {date.dateEnd && date.dateEnd !== date.dateStart ? `to ${date.dateEnd}` : ''}
                        </span>
                      )}

                      {date.status && date.status !== 'auto' && (
                        <span
                          style={{
                            padding: '2px 7px',
                            borderRadius: '6px',
                            fontSize: '10.5px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            background: date.status === 'active' ? '#dcfce7' : date.status === 'completed' ? '#e2e8f0' : '#eff6ff',
                            color: date.status === 'active' ? '#166534' : date.status === 'completed' ? '#475569' : '#1d4ed8'
                          }}
                        >
                          {date.status === 'active' ? 'Active Now' : date.status === 'completed' ? 'Completed' : 'Upcoming'}
                        </span>
                      )}

                      {isPendingDelete ? (
                        <span 
                          className="unified-pending-pill" 
                          style={{ padding: '2px 8px', fontSize: '10px', background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5' }}
                        >
                          <Trash2 size={10} /> Pending Deletion
                        </span>
                      ) : isPendingAdd ? (
                        <span 
                          className="unified-pending-pill" 
                          style={{ padding: '2px 8px', fontSize: '10px', background: '#dcfce7', color: '#15803d', border: '1px solid #86efac' }}
                        >
                          <Plus size={10} /> Proposed New
                        </span>
                      ) : isPending ? (
                        <span className="unified-pending-pill" style={{ padding: '2px 8px', fontSize: '10px' }}>
                          <Clock size={10} /> Pending Review
                        </span>
                      ) : null}
                    </div>

                    <p
                      style={{
                        margin: 0,
                        fontSize: '13px',
                        color: '#475569',
                        lineHeight: 1.5,
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {date.description || 'No description entered.'}
                    </p>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                    <button
                      type="button"
                      className="unified-secondary-btn"
                      onClick={() => handleOpenEditDate(idx)}
                      disabled={isPendingDelete || isPending}
                    >
                      <Edit3 size={14} /> Edit
                    </button>
                    {!isPendingDelete && (
                      <button
                        type="button"
                        onClick={() => handleDeleteDate(idx)}
                        disabled={submitting[`del_date_${idx}`] || isPendingDelete}
                        style={{
                          padding: '8px 12px',
                          color: '#ef4444',
                          background: '#fef2f2',
                          border: '1px solid #fecaca',
                          borderRadius: '8px',
                          cursor: (submitting[`del_date_${idx}`] || isPendingDelete) ? 'not-allowed' : 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontWeight: 600,
                          fontSize: '12px'
                        }}
                        title="Propose deletion of this milestone"
                      >
                        <Trash2 size={14} />
                        <span>{submitting[`del_date_${idx}`] ? 'Submitting...' : 'Delete'}</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add / Edit Milestone Modal */}
      {(isAddingDate || editingDateIndex !== null) && modalDateData && (
        <div
          className="unified-modal-overlay"
          onClick={handleCloseModal}
        >
          <div className="unified-modal" style={{ maxWidth: '640px' }} onClick={(e) => e.stopPropagation()}>
            <div className="unified-modal-header">
              <div>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 800, color: '#0f172a' }}>
                  {isAddingDate ? 'Add Timeline Milestone' : `Edit Milestone: ${modalDateData.title || `Milestone ${(editingDateIndex || 0) + 1}`}`}
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Configure title, month/period badge, start and end dates, and details
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            </div>

            <div className="unified-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Title & Tag */}
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Milestone Title *
                  </label>
                  <input
                    type="text"
                    className="unified-input"
                    value={modalDateData.title || ''}
                    onChange={(e) => handleModalDateChange('title', e.target.value)}
                    placeholder="e.g. Applications Open or Assessment Week"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Short Tag / Month
                  </label>
                  <input
                    type="text"
                    className="unified-input"
                    value={modalDateData.tag || ''}
                    onChange={(e) => handleModalDateChange('tag', e.target.value)}
                    placeholder="e.g. Jan, Feb, or May"
                  />
                </div>
              </div>

              {/* Start & End Dates */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Start Date / Range Label
                  </label>
                  <input
                    type="text"
                    className="unified-input"
                    value={modalDateData.dateStart || ''}
                    onChange={(e) => handleModalDateChange('dateStart', e.target.value)}
                    placeholder="e.g. 2026-01-15 or Mid January"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    End Date (Optional)
                  </label>
                  <input
                    type="text"
                    className="unified-input"
                    value={modalDateData.dateEnd || ''}
                    onChange={(e) => handleModalDateChange('dateEnd', e.target.value)}
                    placeholder="e.g. 2026-02-28"
                  />
                </div>
              </div>

              {/* Status Override */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                  Milestone Status
                </label>
                <select
                  className="unified-input"
                  value={modalDateData.status || 'auto'}
                  onChange={(e) => handleModalDateChange('status', e.target.value)}
                >
                  <option value="auto">Auto (Calculate based on current month & tag)</option>
                  <option value="upcoming">Upcoming (Scheduled in future)</option>
                  <option value="active">Active Now (Currently open/in progress)</option>
                  <option value="completed">Completed (Milestone has passed)</option>
                </select>
              </div>

              {/* Description */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                  Description / Milestone Instructions
                </label>
                <textarea
                  className="unified-textarea"
                  rows={3}
                  value={modalDateData.description || ''}
                  onChange={(e) => handleModalDateChange('description', e.target.value)}
                  placeholder="Details on what happens during this admission date..."
                />
              </div>
            </div>

            <div className="unified-modal-footer">
              <button
                type="button"
                className="unified-secondary-btn"
                onClick={handleCloseModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="unified-submit-button"
                disabled={submitting[isAddingDate ? 'add_date' : `date_${editingDateIndex}`] || !modalDateData.title?.trim()}
                onClick={handleSaveDateModal}
              >
                <Save size={14} />
                {submitting[isAddingDate ? 'add_date' : `date_${editingDateIndex}`]
                  ? 'Submitting...'
                  : (isAddingDate ? 'Submit New Milestone' : 'Save & Submit Date')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
